import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from '@growdash/design-system/atoms';

import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpRight, Bookmark } from 'lucide-react';
// @ts-ignore
import { Badge, Rating } from '@growdash/design-system/atoms';
import { Brand } from '@growdash/growintel/generated';
import BrandLink from '../../components/atoms/brand-link';

// ----------------------------------------------------------------------

type Params = {
  currency: string;
  setActiveRemoveId: (id: string) => void;
};

const NotAvailableText = () => <span className="text-tertiary">N/A</span>;

const columns: (params: Params) => ColumnDef<Brand>[] = ({
  currency,

  setActiveRemoveId,
}) => [
  {
    accessorKey: 'logo',
    header: 'Brand',
    cell: ({ row }) => (
      <BrandLink id={row.original.id ?? ''}>
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarFallback>
              {row.original.brand_name?.charAt(0).toUpperCase()}
            </AvatarFallback>
            <AvatarImage
              src={row.original.brand_image ?? ''}
              alt={row.original.brand_name}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            {row.original.brand_name?.charAt(0).toUpperCase()}
          </Avatar>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="max-w-[150px] truncate">
                  {row.original.brand_name}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex items-center gap-2">
                  {row.original.brand_name}
                  <ArrowUpRight size={14} />
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </BrandLink>
    ),
  },
  {
    accessorKey: 'total_branches',
    header: '# Branches',
  },
  {
    accessorKey: 'cuisines',
    header: 'Cuisine',
    cell: ({ row }) => {
      const cuisinesArray = row.original.cuisines.split(',');
      return (
        <div className="flex items-center gap-2">
          {cuisinesArray.slice(0, 2).map((c) => (
            <Badge variant="secondary">{c}</Badge>
          ))}
          {cuisinesArray.length > 2 && (
            <p className="text-[#72778A] font-semibold">
              +{cuisinesArray.length - 2}
            </p>
          )}
        </div>
      );
    },
  },

  {
    accessorKey: 'avg_rating_score',
    header: 'Rating',
    cell: ({ row }) => {
      const rating = row.original.avg_rating_score;
      const ratingCount = row.original.total_rating_count;
      return (
        <div className="flex items-center gap-1 ">
          {rating !== null && rating !== undefined ? (
            <div className="p">
              <Rating size="small" value={rating} />
            </div>
          ) : (
            <div className="flex justify-center text-muted-foreground">-</div>
          )}
          {ratingCount !== null && ratingCount !== undefined ? (
            <Typography className="text-sm">
              {ratingCount > 0 ? ratingCount.toLocaleString('en-EN') : 0}
            </Typography>
          ) : (
            ''
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'avg_price',
    header: 'Avg Price',
    cell: ({ row }) => {
      const { avg_price } = row.original;
      return avg_price ? (
        `${currency} ${row.original.avg_price}`
      ) : (
        <NotAvailableText />
      );
    },
  },
  {
    accessorKey: '',
    header: 'Saved',
    cell: ({ row }) => (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger>
            <div
              className="cursor-pointer hover:opacity-90"
              onClick={() => {
                setActiveRemoveId(row.original.id!);
              }}
            >
              <TooltipContent side="left">Remove from watchlist</TooltipContent>

              <Bookmark
                size={20}
                className="text-support-orange fill-support-orange"
              />
            </div>
          </TooltipTrigger>
        </Tooltip>
      </TooltipProvider>
    ),
  },
];

export default columns;
